import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import type { ChartView } from '../../shared/utils/chartModel'
import { AxisInterval } from 'recharts/types/util/types'
import { useXAxisReturnType } from '../hooks/use-x-axis-ticks'

type StepUnit = moment.unitOfTime.DurationConstructor

/**
 * Generates ticks and format based on the date settings for zoomed-in views
 */
export const generateZoomedAxisConfig = (
  from: Moment,
  to: Moment,
  view: ChartView,
  isUpMd: boolean,
  isUpSm: boolean,
  isUpTiny: boolean
): useXAxisReturnType => {
  const duration = moment.duration(to.diff(from))
  const isWeekDuration = duration.asWeeks() === 1
  /** Special case for when custom range is selected and exactly one week zoomed data exist */
  const isSpecialCaseWeek = view === 'week' && isWeekDuration
  const stepMultiplier = getStepFactor(duration, view, isUpMd, isUpSm, isUpTiny, false, !isSpecialCaseWeek)
  const stepUnit = getStepUnit(view, !isSpecialCaseWeek)
  const stepSize = applyZoomFactor(view, stepMultiplier)
  const format = getTickFormat(view, !isWeekDuration)
  const ticks = generateTicks(from, to, view, stepUnit, stepSize)

  return { ticks, format }
}

/**
 * Generates ticks and format based on the date settings for normal (non-zoomed) views
 */
export const generateNormalAxisConfig = (
  from: Moment,
  to: Moment,
  view: ChartView,
  isUpMd: boolean,
  isUpSm: boolean,
  isUpTiny: boolean,
  isCustomRangeEnabled?: boolean
): useXAxisReturnType => {
  const duration = moment.duration(to.diff(from))
  const stepUnit = getStepUnit(view, false)
  const stepSize = getStepFactor(duration, view, isUpMd, isUpSm, isUpTiny, isCustomRangeEnabled ?? false)
  const ticks = generateTicks(from, to, view, stepUnit, stepSize)
  const format = getTickFormat(view)
  return { ticks, format }
}

/**
 * Gets the interval determining if the outer ticks should be enforced to be shown
 */
export const getInterval = (view: ChartView): AxisInterval | undefined => {
  if (view === 'day') return undefined
  if (view === 'year') return 0
  return 'preserveStartEnd'
}

/**
 * Generates ticks and format based on the date settings for normal (non-zoomed) views
 */
export const generateTicks = (from: Moment, to: Moment, view: ChartView, stepUnit: StepUnit, stepSize: number): number[] => {
  const currentTS = moment(from)
  const ticks = []

  if (['year', 'month', 'week'].includes(view)) currentTS.startOf(stepUnit)

  while (currentTS.isBefore(to)) {
    ticks.push(currentTS.valueOf())
    currentTS.add(stepSize, stepUnit)
  }

  return ticks
}

/**
 * The step unit determines the tick type (e.g. each tick represents a day like 13.04, ...)
 * for zoomed data, smaller units are used (e.g. hours or minutes) in some views
 */
export const getStepUnit = (view: ChartView, isZoomedIn?: boolean): StepUnit => {
  switch (view) {
    case 'year':
      return 'month'
    case 'month':
      return 'day'
    case 'week':
      return isZoomedIn ? 'hours' : 'day'
    case 'day':
      return isZoomedIn ? 'minutes' : 'hours'
    default:
      return 'hours'
  }
}

/**
 * Gets the step size for the day view based on screen size
 */
export const getDayStepSize = (isUpSm: boolean, isUpTiny: boolean): number => {
  if (isUpSm) return 2
  if (isUpTiny) return 3
  return 4
}

/**
 * Gets the step size for the year view based on screen size
 */
export const getYearStepSize = (isUpMd: boolean, isUpTiny: boolean): number => {
  if (isUpMd) return 1
  if (isUpTiny) return 2
  return 3
}

/**
 * Get the format for the x-axis tick labels
 */
export const getTickFormat = (view: ChartView, isLessThanAWeek?: boolean): string => {
  switch (view) {
    case 'hour':
      return 'HH:mm'
    case 'year':
      return 'MM.YYYY'
    case 'month':
      return 'DD.MM'
    case 'week':
      return isLessThanAWeek ? 'HH:mm' : 'DD.MM'
    case 'day':
    default:
      return 'HH:mm'
  }
}

/**
 * applies a zoom factor to the step size for zoomed-in views
 * 15 minutes for day view, because the step unit for zoomed-in day view is minutes
 */
export const applyZoomFactor = (view: ChartView, multiplier: number): number => {
  if (view === 'day') return 15 * multiplier
  return multiplier
}

/**
 * Determines step multiplier based on zoom duration and view and screen size
 * (e.g. 1 for 1h ticks in day and week view,1 for 1d ticks in month view, 1 for 1 month ticks in year view)
 */
export const getStepFactor = (
  duration: moment.Duration,
  view: ChartView,
  isUpMd: boolean,
  isUpSm: boolean,
  isUpTiny: boolean,
  isCustomRangeEnabled?: boolean,
  isZoomedIn?: boolean
): number => {
  const durationHours = Math.round(duration.asHours())
  const durationDays = Math.round(duration.asDays())

  if (view === 'day') {
    if ((durationDays === 1 && !isZoomedIn) || isCustomRangeEnabled) return getDayStepSize(isUpSm, isUpTiny)
    if (durationHours <= 1) return 1
    if (durationHours <= 2) return 2
    if (durationHours <= 4) return 4
    if (durationHours < 24) return 8
    if (durationHours > 24 && durationHours <= 36) return 12
    return 24
  }

  if (view === 'week') {
    if ((durationDays === 7 && !isZoomedIn) || isCustomRangeEnabled) return isUpTiny ? 1 : 2
    if (durationHours <= 24) return 2
    if (durationHours <= 72) return 6
    return isUpTiny ? 24 : 48
  }

  if (view === 'month') {
    if (durationDays <= 6) return 1
    if (durationDays <= 12) return 2
    if (durationDays <= 36) return isUpSm ? 3 : 5
    return isUpSm ? 5 : 7
  }

  if (view === 'year') {
    const durationMonths = duration.asMonths()
    if ((durationMonths > 6 && durationMonths < 12) || isCustomRangeEnabled) return getYearStepSize(isUpMd, isUpTiny)
    if (durationMonths < 6) return 2
    if (durationMonths > 12 && durationMonths <= 24) return 2
    return 3
  }

  return 1
}
